import {clsx} from 'clsx'
import {brandColorsFromRegistry} from '@/products/flexible-web-toolkit/colors/registry'
import {
	customBrands,
	customBrandChoices,
} from '@/products/flexible-web-toolkit/blocks/helpers'

const MEDIA_TYPE_VIDEO = 'video'
const MEDIA_TYPE_IMAGE = 'image'

export const getMediaType = (item?: {type?: string}): string =>
	item?.type === MEDIA_TYPE_VIDEO ? MEDIA_TYPE_VIDEO : MEDIA_TYPE_IMAGE

export function getSafeBrandBorderClass(
	brandColor?: string | null,
	fallback = 'border-elanco-blue'
): string {
	if (!brandColor) return fallback
	if (customBrandChoices.includes(brandColor))
		return customBrands[brandColor]?.border || fallback
	if (
		Object.prototype.hasOwnProperty.call(
			brandColorsFromRegistry,
			brandColor
		)
	)
		return `border-${brandColor}`
	return fallback
}

export function getBrandColorValue(brandColor?: string | null): string {
	if (!brandColor) return ''
	if (
		Object.prototype.hasOwnProperty.call(
			brandColorsFromRegistry,
			brandColor
		)
	) {
		return brandColorsFromRegistry[brandColor]?.backgroundColor || ''
	}
	return brandColor // fallback if already a hex value
}

function getMainContainerClasses(
	thumbnailPosition: 'bottom' | 'left' | 'right'
): string {
	const baseClasses = 'relative flex-1'

	switch (thumbnailPosition) {
		case 'bottom':
			return `${baseClasses} order-1 md:order-1`
		case 'right':
			return `${baseClasses} order-1 md:order-1`
		case 'left':
		default:
			return `${baseClasses} order-1 md:order-2`
	}
}

export function getThumbnailContainerClasses(
	thumbnailPosition: 'bottom' | 'left' | 'right',
	fullWidth: boolean
): string {
	const baseClasses = 'flex justify-center'

	// Adjust thumbnail width based on fullWidth setting - using md breakpoint
	const thumbnailWidth = fullWidth ? 'md:w-32' : 'md:w-24' // Wider thumbnails for full-width mode

	switch (thumbnailPosition) {
		case 'bottom':
			return `${baseClasses} order-2 mt-4 md:order-2 md:mt-4 md:w-full md:flex-row`
		case 'right':
			return `${baseClasses} order-2 ml-4 md:order-2 md:ml-4 ${thumbnailWidth} md:flex-col md:justify-start`
		case 'left':
		default:
			return `${baseClasses} order-2 mt-4 md:order-1 md:mt-0 ${thumbnailWidth} md:flex-col md:justify-start`
	}
}

type SliderPosition = 'left' | 'center' | 'right' | 'full'

function getSliderPositionClasses(position: SliderPosition): string {
	const positionClasses: Record<SliderPosition, string> = {
		left: '',
		center: 'mx-auto',
		right: 'ml-auto',
		full: 'w-full',
	}
	return positionClasses[position]
}

export function getSliderContainerClasses(
	position: SliderPosition,
	fullWidth: boolean,
	customClassName?: string
): string {
	const baseClasses = 'pdp-slider-container w-full'
	const positionClasses = getSliderPositionClasses(position)
	const maxWidthClasses = !fullWidth && position !== 'full' ? 'max-w-2xl' : ''

	return clsx(baseClasses, positionClasses, maxWidthClasses, customClassName)
}

export function getSliderLayoutClasses(
	containerClassName?: string,
	thumbnailPosition?: 'bottom' | 'left' | 'right'
): string {
	const baseClasses =
		thumbnailPosition === 'bottom'
			? 'flex flex-col mx-3'
			: 'flex flex-col md:flex-row mx-3'
	return clsx(baseClasses, containerClassName)
}

export function getSliderMainImageClasses(
	thumbnailPosition: 'bottom' | 'left' | 'right',
	mainImageClassName?: string
): string {
	const baseClasses = getMainContainerClasses(thumbnailPosition)
	const paddingClasses = ' px-12 pb-4 md:px-16' // Increased mobile padding for arrow clearance
	return clsx(baseClasses, paddingClasses, mainImageClassName)
}

export function scrollThumbnailToActiveIndex(
	thumbnailContainer: HTMLDivElement,
	activeIndex: number,
	mediaItemsLength: number,
	fullWidth: boolean,
	isHorizontal = false
): void {
	if (mediaItemsLength > 4) {
		const firstThumbnail =
			thumbnailContainer.querySelector('.thumbnail-item')
		const fallbackSize = fullWidth ? 110 : 80

		let itemSize = fallbackSize
		if (firstThumbnail) {
			const element = firstThumbnail as HTMLElement
			itemSize = isHorizontal ? element.offsetWidth : element.offsetHeight
		}

		const containerSize = isHorizontal
			? thumbnailContainer.offsetWidth
			: thumbnailContainer.offsetHeight
		const visibleThumbnails = Math.floor(containerSize / itemSize)

		// Calculate the target scroll position
		// If active index is >= 3, scroll to show it in the visible area
		let targetIndex = Math.max(0, activeIndex - 1)

		// If we're near the end, adjust to show the last set
		const maxStartIndex = Math.max(0, mediaItemsLength - visibleThumbnails)
		targetIndex = Math.min(targetIndex, maxStartIndex)

		// Scroll to the target position
		const targetScrollPosition = targetIndex * itemSize
		thumbnailContainer.scrollTo({
			[isHorizontal ? 'left' : 'top']: targetScrollPosition,
			behavior: 'smooth',
		})
	}
}

export function calculateThumbnailContainerHeight(
	mainContainer: HTMLDivElement
): string {
	const slickContainer = mainContainer.querySelector(
		'.slick-navigation-container'
	)
	if (slickContainer) {
		const sliderHeight = (slickContainer as HTMLElement).offsetHeight
		return `${sliderHeight}px`
	}
	const mainContainerHeight = mainContainer.offsetHeight
	return `${mainContainerHeight - 50}px`
}
